import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { SelectFieldShell, useSelectFieldChrome } from './fieldChrome';

/** Matches Tom Select's `maxOptions` default, which every select in this app inherited
 *  until the react-select migration. See makeMenuList in ./fieldChrome. */
export const DEFAULT_MAX_OPTIONS = 50;


/**
 * The app's standard select. react-select underneath, TomSelect's prop contract on top.
 *
 * Deliberately prop-compatible with the component it replaces
 * (src/components/common/TomSelect.js): same props, same `onChange(name, value)`
 * signature, same wrapper markup. That is what lets ~170 call sites migrate by changing
 * an import rather than a call.
 *
 * Where it differs from TomSelect, and why:
 *
 *  - `dropdownParent` is a string in TomSelect ('body') and a DOM node in react-select.
 *    Any non-null value portals the menu to document.body; null renders it inline.
 *  - The label is wired to the input with htmlFor/inputId. TomSelect rendered a label
 *    with no htmlFor at all, so clicking it focused nothing.
 *  - An `error` message is linked to the input via aria-describedby, so a screen reader
 *    announces it. react-select overwrites a plain aria-describedby prop with its own
 *    value, so the two ids have to be composed inside an Input override.
 *  - A `value` with no matching option renders as nothing selected, exactly as
 *    TomSelect's setValue() did for an unknown id. The value itself is untouched — no
 *    onChange fires — so a late-loading options list still resolves to its label once it
 *    arrives.
 */

/** react-select needs {value,label}; call sites supply arbitrary shapes via
 *  valueField/labelField. The raw option is kept for searching and for onChange. */
const toOption = (raw, valueField, labelField) => {
  const value = String(raw?.[valueField] ?? '');
  const rawLabel = raw?.[labelField];
  return {
    value,
    // A missing label would render an invisible, unpickable row.
    label: String(rawLabel == null || rawLabel === '' ? value : rawLabel),
    raw,
  };
};

/**
 * TomSelect sorted its options by default (`{ field: 'name', direction: 'asc' }`);
 * react-select never reorders. Both behaviours have to be reproduced, including
 * `{ field: '$order' }`, which is how a caller opted out — '$order' is TomSelect's
 * internal insertion-order field, so sorting by it is a no-op.
 */
export const sortOptions = (options, sortField) => {
  const field = sortField?.field;
  if (!field || field === '$order') return options;
  // A field no option carries would make every comparison equal; skip rather than
  // paying for a sort that cannot change anything.
  if (!options.some((option) => option.raw?.[field] != null)) return options;
  const direction = sortField?.direction === 'desc' ? -1 : 1;
  return [...options].sort((a, b) => {
    const left = a.raw?.[field];
    const right = b.raw?.[field];
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;
    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * direction;
    }
    return String(left).localeCompare(String(right)) * direction;
  });
};

/** Honours TomSelect's comma-separated `searchField` by matching the raw option's
 *  fields, not just the rendered label (e.g. searchField="name,code" lets an operator
 *  type a code that is not shown). */
const makeFilterOption = (searchField) => {
  const fields = String(searchField || '')
    .split(',')
    .map((field) => field.trim())
    .filter(Boolean);

  return (candidate, rawInput) => {
    const input = String(rawInput || '').trim().toLowerCase();
    if (!input) return true;
    const haystack = [candidate.label, ...fields.map((field) => candidate.data?.raw?.[field])]
      .filter((part) => part != null)
      .map((part) => String(part).toLowerCase());
    return haystack.some((part) => part.includes(input));
  };
};

const AppSelect = ({
  name,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  valueField = 'id',
  labelField = 'name',
  searchField = 'name',
  required = false,
  error = null,
  disabled = false,
  label = '',
  sortField = { field: 'name', direction: 'asc' },
  create = false,
  multiple = false,
  dropdownParent = 'body',
  isLoading = false,
  clearable = false,
  maxOptions = DEFAULT_MAX_OPTIONS,
  className = '',
  id,
  'aria-describedby': ariaDescribedBy = null,
}) => {
  const {
    inputId, errorId, selectComponents, styles, screenReaderStatus,
  } = useSelectFieldChrome({
    id, error, ariaDescribedBy, required, maxOptions,
  });

  const mappedOptions = useMemo(
    () => sortOptions(
      (options || []).map((raw) => toOption(raw, valueField, labelField)),
      sortField
    ),
    // sortField is an object literal at most call sites, so a new identity arrives on
    // every render; comparing its contents keeps the memo useful. Bare disable, not a
    // named one — react-hooks/exhaustive-deps is not registered in the CRA build's
    // ESLint config and naming it fails the build.
    // eslint-disable-next-line
    [options, valueField, labelField, sortField?.field, sortField?.direction]
  );

  // Values are compared as strings throughout: TomSelect stringified everything
  // internally, so call sites pass numbers for what are string option values and expect
  // them to match. react-select compares option identity, which would silently miss.
  const selected = useMemo(() => {
    // First occurrence wins for a duplicated value, matching TomSelect's addOption(),
    // which ignored a second option registered under an existing id. Building the Map
    // straight from the array would let the last duplicate overwrite the first.
    const byValue = new Map();
    mappedOptions.forEach((option) => {
      if (!byValue.has(option.value)) byValue.set(option.value, option);
    });
    if (multiple) {
      const values = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);
      return values
        .map((entry) => byValue.get(String(entry)))
        .filter(Boolean);
    }
    if (value == null || value === '') return null;
    return byValue.get(String(value)) || null;
  }, [mappedOptions, value, multiple]);

  const handleChange = useCallback((next) => {
    if (multiple) {
      // Always an array, never null — call sites map over this without guarding.
      onChange(name, (next || []).map((option) => option.value));
      return;
    }
    // Clearing yields '' rather than null, which is what the native select produced and
    // what every existing handler and payload builder expects.
    onChange(name, next?.value ?? '');
  }, [multiple, name, onChange]);

  const SelectComponent = create ? CreatableSelect : Select;

  return (
    <SelectFieldShell
      label={label}
      required={required}
      inputId={inputId}
      error={error}
      errorId={errorId}
      className={className}
    >
      <SelectComponent
        inputId={inputId}
        name={name}
        classNamePrefix="app-select"
        className={error ? 'is-invalid' : undefined}
        styles={styles}
        components={selectComponents}
        screenReaderStatus={screenReaderStatus}
        isMulti={multiple}
        options={mappedOptions}
        value={selected}
        onChange={handleChange}
        filterOption={makeFilterOption(searchField)}
        isDisabled={disabled}
        isLoading={isLoading}
        // Off by default for parity: TomSelect rendered no clear button, and turning one
        // on across ~170 call sites would be a UI change nobody asked for. Filters that
        // want one opt in per call site.
        isClearable={clearable}
        placeholder={placeholder}
        menuPlacement="auto"
        menuPortalTarget={
          dropdownParent != null && typeof document !== 'undefined' ? document.body : null
        }
        // Multi keeps the menu open between picks and drops chosen entries from the list,
        // which is how TomSelect's multi + remove_button behaved.
        closeMenuOnSelect={!multiple}
        hideSelectedOptions={multiple}
      />
    </SelectFieldShell>
  );
};

AppSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  valueField: PropTypes.string,
  labelField: PropTypes.string,
  searchField: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  sortField: PropTypes.object,
  create: PropTypes.bool,
  multiple: PropTypes.bool,
  dropdownParent: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  isLoading: PropTypes.bool,
  clearable: PropTypes.bool,
  maxOptions: PropTypes.number,
  className: PropTypes.string,
  id: PropTypes.string,
  'aria-describedby': PropTypes.string,
};

export default AppSelect;
